import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { Loader } from '@/core/components/component-library/Icon/loader';
import { GhostModeLayout } from '@/core/components/layout/ghost-mode-layout';
import {
  clearImpersonatedUser,
  getImpersonatedUserFromStorage,
  setImpersonatedUser as setImpersonatedUserUtils,
} from '@/core/libs/impersonation-utils';
import { User } from '@/generated/api';

import { useAuthenticationContext } from './AuthenticationContext';

export interface GhostModeContext {
  impersonatedUser: User | null;
  isLoadingImpersonation: boolean;
  setImpersonatedUser: React.Dispatch<React.SetStateAction<User | null>>;
}

const GhostModeContext = createContext<GhostModeContext>({
  impersonatedUser: null,
  isLoadingImpersonation: false,
  setImpersonatedUser: {} as React.Dispatch<React.SetStateAction<User | null>>,
});

export interface GhostModeContextProviderProps {
  children: JSX.Element;
}

export function GhostModeProvider({
  children,
}: GhostModeContextProviderProps): JSX.Element {
  const { getUserData, currentUser } = useAuthenticationContext();
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [isLoadingImpersonation, setIsLoadingImpersonation] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const prevImpersonatedUser = useRef<User | null>(null);

  useEffect(() => {
    if (!impersonatedUser) {
      // Check localStorage for initial impersonated user state
      const storedUser = getImpersonatedUserFromStorage();

      if (storedUser) {
        setImpersonatedUser(storedUser);
        prevImpersonatedUser.current = storedUser;
      }
    }

    setIsInitialized(true);
  }, [currentUser?.role, impersonatedUser]);

  useEffect(() => {
    const loadImpersonatedUser = async (): Promise<void> => {
      setIsLoadingImpersonation(true);
      if (impersonatedUser) {
        setImpersonatedUserUtils(impersonatedUser);
        prevImpersonatedUser.current = impersonatedUser;
      } else {
        clearImpersonatedUser();
        if (prevImpersonatedUser.current) {
          prevImpersonatedUser.current = null;
        }
      }
      await getUserData().finally(() => {
        setIsLoadingImpersonation(false);
      });
    };

    // Only run for manual impersonation changes, not initial load
    if (isInitialized && impersonatedUser !== prevImpersonatedUser.current) {
      loadImpersonatedUser();
    }
  }, [getUserData, impersonatedUser, isInitialized]);

  // Don't render children until impersonation is properly initialized
  if (!isInitialized) {
    return (
      <div className="bg-gray-50 flex h-screen w-screen items-center justify-center">
        <Loader size="lg" showText />
      </div>
    );
  }

  return (
    <GhostModeContext.Provider
      value={{
        impersonatedUser,
        isLoadingImpersonation,
        setImpersonatedUser,
      }}
    >
      <GhostModeLayout>{children}</GhostModeLayout>
    </GhostModeContext.Provider>
  );
}

export function useGhostModeContext(): GhostModeContext {
  return useContext(GhostModeContext);
}
